import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/app/dialog/dialog";
import ImageUrlProfile from "components/app/image-url/ImageUrlProfile";
import { LoaderCircle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { useEffect } from "react";
import ButtonFollow from "components/app/button-follow/ButtonFollow";
import { profileVerified } from "assets";
import { postedTimeAgo } from "utils/formatTimeSecondToHour";
import { AppLog } from "services";
import { PiShareFatLight } from "react-icons/pi";
import { FaRegCommentAlt } from "react-icons/fa";
import { CgEye } from "react-icons/cg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";
import { Virtuoso } from "react-virtuoso";
import { PostReaction } from "models/postReaction";
import { ShimmerDialogPostReaction } from "../shrimmer/Shrimmer";
import { StatusMsg } from "utils";
import { Tag, useDialogPostReactionStore } from "store/dialog/useDialogPostReactionStore";

interface DialogProps {
  post: any;
  isDialogOpen: boolean;
  postReaction: PostReaction;
  setIsDialogOpen: (value: boolean) => void;
}

export default function DailogPostReaction({ post, isDialogOpen, postReaction, setIsDialogOpen }: DialogProps) {
  const { t } = useTranslation();

  const { likes, shares, comments, views, likeAppRecord, shareAppRecord, commentAppRecord, viewAppRecord, initializePage, fetchPostReactions, onFollowingUser, disposePage } =
    useDialogPostReactionStore();

  const _onClickedFollow = async (userId: string, tag: Tag) => {
    onFollowingUser(userId, tag);
  };

  const loadMoreItems = (tag: Tag) => {
    if (tag === "like") {
      if (likeAppRecord.canLoadMore() === false) return;
    } else if (tag === "share") {
      if (shareAppRecord.canLoadMore() === false) return;
    } else if (tag === "comment") {
      if (commentAppRecord.canLoadMore() === false) return;
    } else if (tag === "view") {
      if (viewAppRecord.canLoadMore() === false) return;
    }

    setTimeout(async () => {
      const postId = post?.provider_post_id.toString();
      fetchPostReactions(postId, tag, false);
    }, 1000);
  };

  useEffect(() => {
    if (isDialogOpen === false) {
      disposePage();
    }
  }, [disposePage, isDialogOpen]);

  useEffect(() => {
    const loadInitialReactions = async () => {
      try {
        const tag = postReaction.value.toLowerCase();
        const postId = post?.provider_post_id.toString();
        initializePage(postId, tag as Tag);
      } catch (error) {
        AppLog.show({
          message: `[loadInitialReactions]`,
          status: StatusMsg.error,
          error,
        });
      }
    };

    if (post && isDialogOpen) {
      loadInitialReactions();
    }
  }, [initializePage, isDialogOpen, post, postReaction.value]);

  const _buildListReaction = (tag: Tag, user: any) => {
    return (
      <div className="flex flex-row items-start space-x-2 w-full pb-1">
        <ImageUrlProfile user={user} style={{ width: 35, height: 35 }} className="rounded-full object-cover border shadow-md " isLinkProfile={true} />
        <div className="flex flex-row justify-between items-center w-full">
          <div className="flex flex-col">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium ">{user.name}</p>
              {user.is_verified === "Yes" && <img src={profileVerified} alt="" style={{ width: 15, height: 15 }} />}

              {user.is_active !== "Yes" && (
                <p className="text-xs text-muted-foreground">
                  `$
                  {postedTimeAgo(user.last_active_datetime_human)}`
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <p className="text-xs text-muted-foreground">{user.username}</p>
            </div>
          </div>
        </div>

        {/* Button info */}
        <div className="flex justify-center">
          <div className="flex justify-center items-center space-x-2 pr-2">
            <ButtonFollow className="h-[25px]" friendStatus={user.friend_status} onClick={() => _onClickedFollow(user.id.toString(), tag)} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isDialogOpen}>
      <DialogContent isShowBtnXClose={false} className="custom-dialog-class">
        <DialogHeader>
          <DialogTitle className="text-center ">{t("Post Reactions")}</DialogTitle>

          <div
            className="absolute flex items-center justify-center right-3 top-1 z-40 cursor-pointer rounded-full hover:bg-accent hover:text-accent-foreground w-[36px] h-[36px] p-2"
            onClick={() => setIsDialogOpen(false)}
          >
            <X />
          </div>
        </DialogHeader>

        <div className=" max-w-[300px] md:max-w-[500px] lg:max-w-[700px]  items-center">
          <Tabs defaultValue={postReaction.value.toLowerCase()} className="w-full">
            {/* Header tab */}
            <TabsList className="grid w-full grid-cols-4 mb-5">
              <TabsTrigger value="like">
                <FontAwesomeIcon icon={solidHeart} size="lg" className=" mr-2" />
                <p>{likeAppRecord.totalRecord}</p>
              </TabsTrigger>
              <TabsTrigger value="share">
                <PiShareFatLight className="text-lg mr-2" />
                <p>{shareAppRecord.totalRecord}</p>
              </TabsTrigger>
              <TabsTrigger value="comment">
                <FaRegCommentAlt className="text-lg mr-2" />
                <p>{commentAppRecord.totalRecord}</p>
              </TabsTrigger>
              <TabsTrigger value="view">
                <CgEye className="text-lg mr-2" />
                <p>{viewAppRecord.totalRecord}</p>
              </TabsTrigger>
            </TabsList>

            {/* like */}
            <TabsContent value="like">
              <div className="flex flex-col h-[500px]">
                {likeAppRecord.isLoading() ? (
                  <ShimmerDialogPostReaction />
                ) : (
                  <Virtuoso
                    data={likes}
                    endReached={() => {
                      loadMoreItems("like");
                    }}
                    overscan={10}
                    itemContent={(index, like) => (
                      <div key={like.id} className=" h-[50px]">
                        {_buildListReaction("like", like)}
                      </div>
                    )}
                    components={{
                      Footer: () =>
                        likeAppRecord.isLoadingMore() ? (
                          <div className="w-full flex justify-center items-center pt-2">
                            <LoaderCircle size={30} className="animate-spin text-yellow-400" />
                          </div>
                        ) : null,
                    }}
                  />
                )}
              </div>
            </TabsContent>

            {/* share */}
            <TabsContent value="share">
              <div className="flex flex-col h-[500px]">
                {shareAppRecord.isLoading() ? (
                  <ShimmerDialogPostReaction />
                ) : (
                  <Virtuoso
                    data={shares}
                    endReached={() => {
                      loadMoreItems("share");
                    }}
                    overscan={10}
                    itemContent={(index, share) => (
                      <div key={share.id} className=" h-[50px]">
                        {_buildListReaction("share", share)}
                      </div>
                    )}
                    components={{
                      Footer: () =>
                        shareAppRecord.isLoadingMore() ? (
                          <div className="w-full flex justify-center items-center pt-2">
                            <LoaderCircle size={30} className="animate-spin text-yellow-400" />
                          </div>
                        ) : null,
                    }}
                  />
                )}
              </div>
            </TabsContent>

            {/* comment */}
            <TabsContent value="comment">
              <div className="flex flex-col h-[500px]">
                {commentAppRecord.isLoading() ? (
                  <ShimmerDialogPostReaction />
                ) : (
                  <Virtuoso
                    data={comments}
                    endReached={() => {
                      loadMoreItems("comment");
                    }}
                    overscan={10}
                    itemContent={(index, comment) => (
                      <div key={comment.id} className=" h-[50px]">
                        {_buildListReaction("comment", comment)}
                      </div>
                    )}
                    components={{
                      Footer: () =>
                        commentAppRecord.isLoadingMore() ? (
                          <div className="w-full flex justify-center items-center pt-2">
                            <LoaderCircle size={30} className="animate-spin text-yellow-400" />
                          </div>
                        ) : null,
                    }}
                  />
                )}
              </div>
            </TabsContent>

            {/* view */}
            <TabsContent value="view">
              <div className="flex flex-col h-[500px]">
                {viewAppRecord.isLoading() ? (
                  <ShimmerDialogPostReaction />
                ) : (
                  <Virtuoso
                    data={views}
                    endReached={() => {
                      loadMoreItems("view");
                    }}
                    overscan={10}
                    itemContent={(index, view) => (
                      <div key={view.id} className=" h-[50px]">
                        {_buildListReaction("view", view)}
                      </div>
                    )}
                    components={{
                      Footer: () =>
                        viewAppRecord.isLoadingMore() ? (
                          <div className="w-full flex justify-center items-center pt-2">
                            <LoaderCircle size={30} className="animate-spin text-yellow-400" />
                          </div>
                        ) : null,
                    }}
                  />
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}

